import { http } from '@/lib/http';
import { WORKDAY_INTEGRATION_TYPE } from './constants';
import type {
  CreatePositionPayload,
  CreatePositionResult,
  KomboEnvelope,
  PositionReferenceOptionsResponse,
  PositionStatusResult,
} from './types';

// `@/lib/http` is the `/api/v1` instance every other Kombo call uses; its
// interceptor already unwraps to the `{ data, errors }` envelope.

interface ReferenceOptionsParams {
  /** Workday reference-ID type, e.g. `Company_Reference_ID`. */
  type: string;
}

/**
 * Lists are cached server-side for 15 minutes. The endpoint also accepts
 * `refresh=true` to bypass that, but nothing calls it — a reference created in
 * Workday in the last 15 minutes will not appear until the cache expires.
 */
const getPositionReferenceOptions = async ({
  type,
}: ReferenceOptionsParams): Promise<
  KomboEnvelope<PositionReferenceOptionsResponse>
> =>
  http.get('kombo/position_reference_options', {
    params: {
      integration_type: WORKDAY_INTEGRATION_TYPE,
      type,
    },
  });

/**
 * Creates the position in Workday synchronously (1–2s). Submitting twice is
 * safe — the second call returns the existing position — but callers should
 * still disable submit while in flight.
 */
const createPosition = async (
  payload: Omit<CreatePositionPayload, 'integration_type'>
): Promise<KomboEnvelope<CreatePositionResult>> =>
  http.post('kombo/create_position', {
    integration_type: WORKDAY_INTEGRATION_TYPE,
    ...payload,
  });

/**
 * The only call that moves `syncing` → `ready`: `show` reports stored state, so
 * without this it says `syncing` forever. Not polled — Kombo's sync takes 4–5
 * minutes and cannot be hurried.
 */
const getPositionStatus = async (
  atsApplicationId: string | number
): Promise<KomboEnvelope<PositionStatusResult>> =>
  http.get('kombo/position_status', {
    params: {
      integration_type: WORKDAY_INTEGRATION_TYPE,
      ats_application_id: atsApplicationId,
    },
  });

export const WORKDAY_POSITION_API = {
  getPositionReferenceOptions,
  createPosition,
  getPositionStatus,
};

export const WORKDAY_POSITION_QUERY_KEYS = {
  status: (atsApplicationId: string | number) =>
    ['workdayPosition', 'status', String(atsApplicationId)] as const,
  referenceOptions: (type: string) =>
    ['workdayPosition', 'referenceOptions', type] as const,
};

/**
 * The lists are stable for a working session and each field is opened more than
 * once, so they are cached client-side too rather than re-requested per menu
 * open. Shorter than the server's 15 minutes.
 */
const REFERENCE_OPTIONS_STALE_TIME = 5 * 60 * 1000;

/**
 * Shared so the imperative `loadOptions` path and the job-profile default read
 * through **one** cache entry — two keys for the same list would fetch it twice
 * and then disagree about it.
 */
export const positionReferenceOptionsQuery = (type: string) => ({
  queryKey: WORKDAY_POSITION_QUERY_KEYS.referenceOptions(type),
  queryFn: () => getPositionReferenceOptions({ type }),
  staleTime: REFERENCE_OPTIONS_STALE_TIME,
});
